import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { PageLayout } from "../components/PageLayout";
import { BugTicketStatusBadge } from "../components/BugTicketStatusBadge";
import { BugTicketTimeline } from "../components/BugTicketTimeline";
import { Alert, AlertDescription, Button, Card, InputField, Spinner, Text, View } from "../components/base";
import { Icon } from "../components/base/Icon";
import { useAppDispatch, useAppSelector } from "../redux";
import { bugTicketActions, bugTicketCommentActions } from "../redux/reducers/bugTicketSlice";
import { formatRelative } from "../utils/adminFormat";

export const BugReportDetailPage = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const dispatch = useAppDispatch();

  const ticket = useAppSelector((state) => (ticketId ? state.bugTicket.entities[ticketId] : undefined));
  const isLoadingTicket = useAppSelector((state) => state.bugTicket.isLoading);
  const commentState = useAppSelector((state) => state.bugTicketComments);
  const comments = commentState.ids.map((id) => commentState.entities[id]).filter(Boolean);

  const [reply, setReply] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ticketId) return;
    dispatch(bugTicketActions.getById({ id: ticketId }));
    dispatch(bugTicketCommentActions.get({ bugTicketId: ticketId }));
  }, [dispatch, ticketId]);

  const willReopen = ticket?.status === "resolved" || ticket?.status === "closed";

  const handleSendReply = async () => {
    if (!ticketId || !reply.trim()) return;
    setIsSending(true);
    setError(null);

    const result = await dispatch(bugTicketCommentActions.create({ bugTicketId: ticketId, body: reply.trim() }));

    if (bugTicketCommentActions.create.fulfilled.match(result)) {
      setReply("");
      // Refresh the thread and the ticket — replying to a resolved/closed
      // ticket re-opens it, and the timeline gains a status entry.
      dispatch(bugTicketCommentActions.get({ bugTicketId: ticketId }));
      dispatch(bugTicketActions.getById({ id: ticketId }));
    } else {
      setError("Failed to send reply. Please try again.");
    }
    setIsSending(false);
  };

  if (!ticket) {
    return (
      <PageLayout
        title="Bug Report"
        backTo="/bug-reports"
        backLabel="Bug reports"
      >
        <View className="flex items-center justify-center py-16">
          {isLoadingTicket ? <Spinner size={28} /> : <Text className="text-sm text-gray-400 dark:text-gray-500">Bug report not found.</Text>}
        </View>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={ticket.title}
      backTo="/bug-reports"
      backLabel="Bug reports"
      headerAction={<BugTicketStatusBadge status={ticket.status} />}
    >
      <View className="mt-6 flex flex-col gap-6 max-w-3xl">
        <Card className="p-4">
          <View className="mb-2 flex flex-row items-center gap-2">
            <Icon
              name="bug_report"
              size={14}
              className="text-gray-400 dark:text-gray-500"
            />
            <Text className="text-xs text-gray-400 dark:text-gray-500">
              Reported {formatRelative(String(ticket.createdAt))} via {ticket.source === "cli" ? "CLI" : "web"}
            </Text>
          </View>
          <Text className="block whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">{ticket.description}</Text>
          {ticket.screenshotUrl && (
            <a
              href={ticket.screenshotUrl}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={ticket.screenshotUrl}
                alt="Bug report screenshot"
                className="mt-3 max-h-64 rounded-lg border border-gray-200 dark:border-gray-700"
              />
            </a>
          )}
        </Card>

        <View className="flex flex-col gap-3">
          <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Conversation</Text>
          <BugTicketTimeline
            comments={comments}
            submitterLabel="You"
          />
        </View>

        <View className="flex flex-col gap-3">
          {willReopen && (
            <Alert variant="warning">
              <AlertDescription>This bug report is {ticket.status === "resolved" ? "resolved" : "closed"}. Sending a reply will re-open it.</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <InputField
            type="textarea"
            value={reply}
            onChange={setReply}
            placeholder="Write a reply…"
            isDisabled={isSending}
          />
          <View className="flex flex-row justify-end">
            <Button
              variant={willReopen ? "warning" : "brand"}
              size="sm"
              onClick={handleSendReply}
              isDisabled={isSending || !reply.trim()}
            >
              {isSending ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin mr-1.5"
                />
              ) : (
                <Icon
                  name="send"
                  size={14}
                  className="mr-1.5"
                />
              )}
              {willReopen ? "Reply & Re-open" : "Send Reply"}
            </Button>
          </View>
        </View>
      </View>
    </PageLayout>
  );
};
